import ApiUtils from "../ApiUtils"
import { PaginatedResponseMini } from "../../interfaces/baseInterfaces"
import { getDoctorTenant } from "../../utils/helpers"
import { IFetchUserAttributesParams, IUpdateTaskAttributesParams, IUpdateTaskAttributesResponse } from "../../interfaces/api/sms/userAttributes"
import { INotifySubscriptionData } from "../../interfaces/api/sms/subscription"
import { IGetTaskRequestStatusRes } from "../../interfaces/api/sms/task"
import { ISubscriptionPack } from "../../interfaces/api/sms/packs"
import { IFetchCallHistoryParams } from "../../interfaces/api/sms/ICallLogs"
import { ISubscriptionManagerApis } from "./ISubscriptionManagerApi"
import { IUserAttributes } from "../../interfaces/patient/IUserAttribute"
import { IAgentUserParam } from "../../interfaces/api/sms/ICallPatient"
import {
	IAudioTaskDaySummary,
	ICreateTaskRequestParams,
	ICreateTaskRequestResponse,
	ISearchCoachTaskRequestsParams,
	ISearchCoachTaskRequestsResponse,
	IUpdateTaskRequestParams,
	IUpdateTaskRequestResponse,
} from "../../interfaces/patient/ITask"
import { AlbusApiUrls } from "../albus/AlbusApis"
import { IAppointmentAudioTask } from "../../interfaces/api/sms/IAppointmentAudioTask"
import { IJourneyTabs } from "../../interfaces/api/sms/coachScriptConfig"
import { WidgetData } from "../../interfaces/ongoingConsultation/ICoachConsultationData"
import {
	IFetchRcaUserListParams,
	IFetchRcaUserListResponse,
	IFetchUserLatestReportParams,
	IFetchUserLatestReportResponse,
	IGetCallStatusSummaryParams,
	IGetCallStatusSummaryResponse,
	IGetRcaOpenActionItemsResponse,
	IGetResponderSegmentHistoryParams,
	IGetResponderSegmentHistoryResponse,
	IGetTaskLogWeeklySummaryParams,
	IGetTaskLogWeeklySummaryResponse,
	IUpdateOpenActionItemsParams,
	IUpdateUserProgressReportParams,
	IUpdateUserProgressReportResponse,
} from "../../interfaces/api/sms/IRCA"
import { map } from "lodash"
import { IAcknowledgePendingLogInsightMarkersParams, IAcknowledgePendingLogInsightMarkersResponse, IGetPendingLogInsightMarkersResponse } from "../../interfaces/api/sms/ILogMarkers"
import { ICgmRequestPayload, ICgmRequestStatus } from "../../interfaces/api/sms/ICgmRequest"
import { IRaiseDiagnosticBookingRequestPayload, IRaiseDiagnosticBookingRequestRes } from "../../interfaces/api/sms/raiseDiagnosticBookingRequest"
import { IFetchAssignedLocumCoachResponse } from "../../interfaces/api/sms/fetchAssignedLocumCoach"
import { IGetOrCreateDummyUserForCoach } from "../../interfaces/api/sms/getOrCreateDummyUserForCoach"
import { IPushCoachEventsParams, IPushCoachEventsResponse } from "../../interfaces/api/sms/pushCoachEvents"
import { IGetCoachTaskSummaryAnalyticsParams, IGetCoachTaskSummmaryAnalyticsParams } from "../../interfaces/api/sms/getCoachTaskSummmaryAnalytics"
import { ISearchPendingTaskProfilesForUserParams, ISearchPendingTaskProfilesForUserResponse } from "../../interfaces/api/sms/searchPendingTaskProfilesForUser"
import { IEditPatientDetailsParams } from "../../interfaces/api/sms/editPatientDetails"
import { ICheckRenewalOffetStatusResponse } from "../../interfaces/api/sms/checkRenewalOfferStatus"
import { IRevokeCoachTaskRequestPayload, IRevokeCoachTaskRequestResponse } from "../../interfaces/api/sms/revokeCoachTaskRequest"
import { ISaveAgentFeedbackPayload, ISaveAgentFeedbackResponse } from "../../interfaces/api/sms/saveAgentFeedback"
import { RenewalCrmType } from "../../constants"
import { IGetLatestReportCardPayload, IGetLatestReportCardResponse } from "../../interfaces/api/sms/getLatestReportCard"
import { IGetReportCardPdfPayload, IGetReportCardPdfResponse } from "../../interfaces/api/sms/getReportCardPdf"

export const SUBSCRIPTION_MANAGER_API_PREFIX = "/subscription-manager"
const SUBSCRIPTION_MANAGER_API_PREFIX_ORION_CONTROLLER = "/internal/subscription-manager"

const SubscriptionManagerApiUrls = {
	userAttributeSearch: (userId: number) => `/v1/user-attributes/search?limit=1&offset=0&query=userId.eq%3A${userId}&sortBy=NULL&sortOrder=ASC`,
	patchWhatsAppNotification: (userId: number) => `/v1/user-attributes/user/${userId}`,
	fetchUserAttributes: (userId: string) => `/v1/user-attributes/?userId=${userId}`,
	agentPatientListFiltersUrl: (agentType: string) => `/agent/filters?agent-type=${agentType}`,
	fetchCallHistoryUrl: (offset: number, endDate: string, limit: number, userId: number) => `/v1/user-interaction-log/emr-call?offset=${offset}&endDate=${endDate}&limit=${limit}&userId=${userId}`,
	taskRequestStatusUrl: (userId: string) => `/coach-task-request/status/?userId=${userId}`,
	patchUpdateTaskAttributesUrl: (userId: string) => `/v1/user-attributes/${userId}/task-attributes`,
	updateUserAttribute: (userId: number) => `/v1/user-attributes/user/${userId}`,
	agentUserCall: `/v1/agent-user-call`,
	createTaskRequest: `/coach-task-request/create/`,
	updateTaskRequest: `/coach-task-request/review/`,
	requestCgm: `/v1/user-subscription/cgm-install-coach-request`,
	coachPendingAudioTaskSummary: `/appointment-audio-task/day-summary`,
	coachPendingAudioTask: `/appointment-audio-task/pending-tasks`,
	searchAudioTaskByAppointment: `/appointment-audio-task/search-by-appointment`,
	coachConsultationConfig: (userId: number, doctorType?: string) => `/v1/emr/ui-config/?userId=${userId}${doctorType ? `&doctorType=${doctorType}` : ""}`,
	coachConsultationConfigDataSave: (userId: number) => `/v1/emr/${userId}/data/`,
	userJourneyBookingStatus: (userId: number) => `/v1/user-journey/booking-status?userId=${userId}`,
	rcaOpenActionItems: (userId: string) => `/v1/progress-report-actions/pending-actions?userId=${userId}`,
	updateOpenActionItems: ({ actionIds, status, updatedBy }: IUpdateOpenActionItemsParams) =>
		`/v1/progress-report-actions/status-update?${map(actionIds, (actionId) => `actionIds=${actionId}`).join("&")}&status=${status}&updatedBy=${updatedBy}`,
	getPendingInsightMarkersUrl: (userId: string) => `/v1/user-marker-ack-log/pending-markers?userId=${userId}`,
	ackPendingInsightLogMarkers: "/v1/user-marker-ack-log/ack-marker",
	getCgmRequestStatus: (userId: string) => `/v1/user-subscription/cgm-install-coach-request/status?userId=${userId}`,
	raiseDiagnosticBookingRequest: "/v1/user-subscription/diagnostic-test-request",
	fetchAssignedLocumCoach: (userId: string) => `/locum-agent-assignment/user/${userId}/assigned-locum`,
	searchCoachTaskRequestUrl: "/coach-task-request/search/v2/",
	getOrCreateDummyUserForCoach: (coachId: number) => `/agent-dummy-user-mapping/search/?coachId=${coachId}`,
	pushCoachEvents: "/emr-events/timer/v1/bulk",
	rcaUserList: `/v1/user-progress-report/user-list`,
	updateUserProgressReport: `/v1/user-progress-report/update`,
	callStatusSummary: `/v1/user-interaction-log/emr-call/status-summary`,
	taskLogWeeklySummary: `/v1/task-daily-log/weekly-summary`,
	responderSegmentHistory: `/v1/user-segment-history/responder-segment/history`,
	userLatestReport: `/v1/user-progress-report/latest-report`,
	getCoachTaskSummmaryAnalytics: (params: IGetCoachTaskSummmaryAnalyticsParams) => `/emr-coach-dashboard/task-summary?${ApiUtils.requestToQueryParams(params)}`,
	searchPendingTaskProfiles: `/v1/user-task-journey/pending`,
	editPatientDetails: (userId: string) => `/v1/nux/${userId}/profile`,
	applyRenewalOffer: "/v1/renewal-journey/apply-offer",
	renewalCrm: (userId: string, renewalCRMType: RenewalCrmType) => `/v1/renewal-journey/renewal-crm?renewalCRMType=${renewalCRMType}&userId=${userId}`,
	checkRenewalOfferStatus: "/v1/renewal-journey/renewal-offer/status",
	revokeTaskRequest: (id: number) => `/coach-task-request/${id}`,
	saveAgentFeedback: "/agent-feedback/",
	getLatestReportCard: "/v1/renewal-report-card/latest",
	getReportCardPdf: "/v1/renewal-report-card/pdf",
	sendProteinUpsellCrm: (userId: string) => `/v1/renewal-journey/coach-protein-crm?userId=${userId}`,
}

class SubscriptionManagerApis implements ISubscriptionManagerApis {
	private getServiceUrl(path: string) {
		return SUBSCRIPTION_MANAGER_API_PREFIX + path
	}

	private getInternalServiceUrl(path: string) {
		return SUBSCRIPTION_MANAGER_API_PREFIX_ORION_CONTROLLER + path
	}
	public getAllPacks(
		limit = 100,
		offset = 0,
		sort: {
			sortBy: string
			sortOrder: "ASC" | "DESC"
		} = { sortBy: "productName", sortOrder: "ASC" }
	): Promise<PaginatedResponseMini<ISubscriptionPack>> {
		const url = this.getServiceUrl("/v1/subscription-pack/search") + "?" + ApiUtils.requestToQueryParams({ limit, offset, sortBy: sort.sortBy, sortOrder: sort.sortOrder })
		return ApiUtils.get(url)
	}


	public fetchWhatsAppNotification(userId: number): Promise<any> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.userAttributeSearch(userId))
		return ApiUtils.get(url)
	}

	patchWhatsAppNotification(userId: number, notifyData: INotifySubscriptionData): Promise<any> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.patchWhatsAppNotification(userId))
		return ApiUtils.patch(url, notifyData as Record<string, any>)
	}
	getOpsBookingStatuses(userId: number): Promise<any> {
		return ApiUtils.get(this.getServiceUrl(SubscriptionManagerApiUrls.userJourneyBookingStatus(userId)))
	}

	updateUserAttributes(userId: number, userAttributes: IUserAttributes): Promise<IUserAttributes> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.updateUserAttribute(userId))
		return ApiUtils.patch(url, userAttributes)
	}
	
	searchDoctorPatients = (doctorId: any, query: any) => {
		const url = this.getInternalServiceUrl(`/patient-list`)
		return ApiUtils.post(url, { ...query, agentId: doctorId }, { x_tenant: getDoctorTenant() })
	}

	fetchAgentPatientListPageFilters = async (agentType: string) => {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.agentPatientListFiltersUrl(agentType))
		return ApiUtils.get(url)
	}

	fetchCallHistory(params: IFetchCallHistoryParams) {
		const { endDate, limit, offset, userId } = params
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.fetchCallHistoryUrl(offset, endDate, limit, Number(userId)))
		return ApiUtils.get(url)
	}
	
	fetchUserAttributes(params: IFetchUserAttributesParams): Promise<IUserAttributes> {
		const { userId } = params
		return ApiUtils.get(this.getServiceUrl(SubscriptionManagerApiUrls.fetchUserAttributes(userId)))
	}
	fetchCoachConsultationConfig(userId: number, doctorType?: string): Promise<IJourneyTabs> {
		// return Promise.resolve(testConfig);
		return ApiUtils.get(this.getServiceUrl(SubscriptionManagerApiUrls.coachConsultationConfig(userId, doctorType)), null, null, null, null, 10000)
	}

	saveCoachConsultationConfigDataSave(userId: number, widgetData: WidgetData) {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.coachConsultationConfigDataSave(userId))
		return ApiUtils.put(url, widgetData)
	}
	

	getCgmRequestStatus(userId: string): Promise<ICgmRequestStatus> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.getCgmRequestStatus(userId))
		return ApiUtils.get(url)
	}

	requestCgm(payload: ICgmRequestPayload): Promise<void> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.requestCgm)
		return ApiUtils.post(url, payload)
	}

	raiseDiagnosticBookingRequest(payload: IRaiseDiagnosticBookingRequestPayload): Promise<IRaiseDiagnosticBookingRequestRes> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.raiseDiagnosticBookingRequest)
		return ApiUtils.post(url, payload)
	}

	getCoachPendingAudioTasks(coachId: number, dateEpoch: number): Promise<IAppointmentAudioTask[]> {
		const queryParamStr = ApiUtils.requestToQueryParams({ coachId, dateEpoch })
		return ApiUtils.get(`${this.getServiceUrl(SubscriptionManagerApiUrls.coachPendingAudioTask)}?${queryParamStr}`, null, null, null, null, 10000)
	}

	getCoachPendingAudioTasksSummary(coachId: number, dateEpoch: number): Promise<IAudioTaskDaySummary> {
		const queryParamStr = ApiUtils.requestToQueryParams({ coachId, dateEpoch })
		return ApiUtils.get(`${this.getServiceUrl(SubscriptionManagerApiUrls.coachPendingAudioTaskSummary)}?${queryParamStr}`)
	}

	searchAudioTaskByAppointment(appointmentId: number): Promise<IAppointmentAudioTask[]> {
		const queryParamStr = ApiUtils.requestToQueryParams({ appointmentId })
		return ApiUtils.get(`${this.getServiceUrl(SubscriptionManagerApiUrls.searchAudioTaskByAppointment)}?${queryParamStr}`, null, null, null, null, 10000)
	}

	searchCoachTaskRequests(params: ISearchCoachTaskRequestsParams): Promise<ISearchCoachTaskRequestsResponse> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.searchCoachTaskRequestUrl)
		return ApiUtils.post(url, params)
	}

	getTaskRequestStatus(userId: string): Promise<IGetTaskRequestStatusRes> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.taskRequestStatusUrl(userId))
		return ApiUtils.get(url)
	}
	createTaskRequest(params: ICreateTaskRequestParams): Promise<ICreateTaskRequestResponse> {
		return ApiUtils.post(this.getServiceUrl(SubscriptionManagerApiUrls.createTaskRequest), params)
	}

	updateTaskRequest(params: IUpdateTaskRequestParams): Promise<IUpdateTaskRequestResponse> {
		return ApiUtils.post(this.getServiceUrl(SubscriptionManagerApiUrls.updateTaskRequest), params)
	}

	patchUpdateTaskAttributes(params: IUpdateTaskAttributesParams): Promise<IUpdateTaskAttributesResponse> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.patchUpdateTaskAttributesUrl(params.userId))
		return ApiUtils.patch(url, params.requestParams as Record<string, any>)
	}
	//
	fetchRcaUserList(params: IFetchRcaUserListParams): Promise<IFetchRcaUserListResponse> {
		return ApiUtils.post(this.getServiceUrl(SubscriptionManagerApiUrls.rcaUserList), params)
	}

	fetchUserLatestReport(params: IFetchUserLatestReportParams): Promise<IFetchUserLatestReportResponse> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.userLatestReport) + "?" + ApiUtils.requestToQueryParams(params)
		return ApiUtils.get(url)
	}

	getTaskLogWeeklySummary(params: IGetTaskLogWeeklySummaryParams): Promise<IGetTaskLogWeeklySummaryResponse> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.taskLogWeeklySummary) + "?" + ApiUtils.requestToQueryParams(params)
		return ApiUtils.get(url)
	}
	//
	getCallStatusSummary(params: IGetCallStatusSummaryParams): Promise<IGetCallStatusSummaryResponse> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.callStatusSummary) + "?" + ApiUtils.requestToQueryParams(params)
		return ApiUtils.get(url)
	}

	getResponderSegmentHistory(params: IGetResponderSegmentHistoryParams): Promise<IGetResponderSegmentHistoryResponse> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.responderSegmentHistory) + "?" + ApiUtils.requestToQueryParams(params)
		return ApiUtils.get(url)
	}
	updateUserProgressReport(params: IUpdateUserProgressReportParams): Promise<IUpdateUserProgressReportResponse> {
		return ApiUtils.post(this.getServiceUrl(SubscriptionManagerApiUrls.updateUserProgressReport), params)
	}
	
	makeAgentUserCall(params: IAgentUserParam): Promise<any> {
		return ApiUtils.post(this.getServiceUrl(SubscriptionManagerApiUrls.agentUserCall), params)
	}

	getRcaOpenActionItems(userId: string): Promise<IGetRcaOpenActionItemsResponse> {
		return ApiUtils.get(this.getServiceUrl(SubscriptionManagerApiUrls.rcaOpenActionItems(userId)))
	}

	updateOpenActionItems(params: IUpdateOpenActionItemsParams): Promise<IGetRcaOpenActionItemsResponse> {
		return ApiUtils.post(this.getServiceUrl(SubscriptionManagerApiUrls.updateOpenActionItems(params)), {})
	}

	getPendingInsightMarkers(userId: string): Promise<IGetPendingLogInsightMarkersResponse> {
		return ApiUtils.get(this.getServiceUrl(SubscriptionManagerApiUrls.getPendingInsightMarkersUrl(userId)))
	}

	acknowledgePendingLogInsightMarkers(params: IAcknowledgePendingLogInsightMarkersParams): Promise<IAcknowledgePendingLogInsightMarkersResponse> {
		const url = this.getServiceUrl(`${SubscriptionManagerApiUrls.ackPendingInsightLogMarkers}?${ApiUtils.requestToQueryParams(params)}`)
		return ApiUtils.post(url, {})
	}

	fetchAssignedLocumCoach(userId: string): Promise<IFetchAssignedLocumCoachResponse> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.fetchAssignedLocumCoach(userId))
		return ApiUtils.get(url)
	}

	getOrCreateDummyUserForCoach(coachId: number): Promise<IGetOrCreateDummyUserForCoach> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.getOrCreateDummyUserForCoach(coachId))
		return ApiUtils.post(url, null)
	}

	pushCoachEvents(params: IPushCoachEventsParams[]): Promise<IPushCoachEventsResponse> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.pushCoachEvents)
		return ApiUtils.post(url, params)
	}

	getCoachTaskSummmaryAnalytics(params: IGetCoachTaskSummmaryAnalyticsParams): Promise<IGetCoachTaskSummaryAnalyticsParams> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.getCoachTaskSummmaryAnalytics(params))
		return ApiUtils.get(url)
	}

	searchPendingTaskProfilesForUser(params: ISearchPendingTaskProfilesForUserParams): Promise<ISearchPendingTaskProfilesForUserResponse[]> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.searchPendingTaskProfiles)
		return ApiUtils.post(url, params)
	}

	editPatientDetails(params: IEditPatientDetailsParams): Promise<any> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.editPatientDetails(params.userId))
		return ApiUtils.post(url, params)
	}

	applyRenewalOffer(userId: string): Promise<any> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.applyRenewalOffer) + "?" + ApiUtils.requestToQueryParams({ userId })
		return ApiUtils.post(url, {})
	}

	renewalCrm(userId: string, renewalCrmType: RenewalCrmType): Promise<any> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.renewalCrm(userId, renewalCrmType))
		return ApiUtils.post(url, {})
	}

	checkRenewalOfferStatus(userId: string): Promise<ICheckRenewalOffetStatusResponse> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.checkRenewalOfferStatus) + "?" + ApiUtils.requestToQueryParams({ userId })
		return ApiUtils.get(url)
	}

	revokeCoachTask(payload: IRevokeCoachTaskRequestPayload): Promise<IRevokeCoachTaskRequestResponse> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.revokeTaskRequest(payload.id))
		delete payload["id"]
		return ApiUtils.patch(url, payload)
	}

	saveAgentFeedback(payload: ISaveAgentFeedbackPayload): Promise<ISaveAgentFeedbackResponse> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.saveAgentFeedback)
		return ApiUtils.post(url, payload)
	}

	getLatestReportCard(payload: IGetLatestReportCardPayload): Promise<IGetLatestReportCardResponse> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.getLatestReportCard) + "?" + ApiUtils.requestToQueryParams(payload)
		return ApiUtils.get(url, payload)
	}

	getReportCardPdf(payload: IGetReportCardPdfPayload): Promise<IGetReportCardPdfResponse> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.getReportCardPdf) + "?" + ApiUtils.requestToQueryParams(payload)
		return ApiUtils.get(url, payload)
	}

	sendProteinUpsellCrm(userId: string): Promise<any> {
		const url = this.getServiceUrl(SubscriptionManagerApiUrls.sendProteinUpsellCrm(userId))
		return ApiUtils.post(url, {})
	}
}

export default SubscriptionManagerApis
